import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';

import { addProductToWishList, removeProductFromWishList, storePendingProductToWishList } from '@/redux/slices/wishList.slice';
import { showConfirmModal, showLoginModal } from '@/redux/slices/gui.slice';
import { useAppSelector, useAppDispatch } from '@/redux';

import { NS } from '@/constants';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { useFeature } from '@/hooks';
import { ProductModel } from '@/models';

import Icon from '@/components/common/iconedLinks/Icon';

import styles from './WishListIcon.module.scss';
import { FEATURES } from '@/constants/features';
import { URLS } from '@/constants/urls';

const SHOW_CONFIRM = {
  TRUE: true,
  FALSE: false
};

const ACTION = {
  ADD: 'add',
  REMOVE: 'delete'
};

const isProductInWishList = (wishList, product) => (
  wishList.includes(product?.partnumber)
);

type WishListIconProps = {
  product: ProductModel;
  withCloseIcon?: boolean;
};

const WishListIcon = ({ product, withCloseIcon = false }: WishListIconProps) => {
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const wishList = useAppSelector(({ wishList }) => wishList?.content);
  const { t } = useTranslation(NS.COMMON);

  const isChecked = useMemo(() => wishList?.length && isProductInWishList(wishList, product), [wishList]);
  const isWishlistFeatureEnabled = useFeature(storeConf, FEATURES.MODULE_WISHLIST);

  const updateWishList = useCallback((event, action, showConfirm) => {
    event.stopPropagation();
    if (isUserEmpty(loggedUser)) {
      dispatch(storePendingProductToWishList({ partnumber: product.partnumber }));
      dispatch(showLoginModal());
    } else if (showConfirm) {
      dispatch(showConfirmModal({
        content: {
          cancel: t('forms.radio.camelFalse'),
          confirm: t('forms.radio.camelTrue'),
          content: t(`account.wishlist.modal.${action}.content`),
          title: t(`btn.${action}`),
          confirmClick: () => update(action)
        }
      }));
    } else {
      update(action);
    }
  }, [loggedUser, product._id]);

  const update = (action) => {
    switch (action) {
      case ACTION.ADD:
        dispatch(addProductToWishList({ partnumber: product.partnumber }));
        break;
      case ACTION.REMOVE:
        dispatch(removeProductFromWishList({ partnumber: product.partnumber }));
        break;
    }
  };

  const rbfIcon = withCloseIcon ? 'close' : isChecked ? 'heart-checked' : 'heart-unchecked';

  return (isWishlistFeatureEnabled && product._id) ? (
    <span
      className={styles.container}
      data-rbf={`wishlist-icon id=[${product._id}] icon=[${rbfIcon}]`}
      data-test-id="icon"
    >
      {withCloseIcon ? (
        <Icon
          src={URLS.ORION_CLOSE_BLACK}
          alt={t('btn.delete')}
          onClick={(e) => updateWishList(e, ACTION.REMOVE, SHOW_CONFIRM.TRUE)}
          height={24}
          width={24}
        />
      ) : (
        <Icon
          onClick={(e) => updateWishList(e, isChecked ? ACTION.REMOVE : ACTION.ADD, SHOW_CONFIRM.FALSE)}
          src={isChecked ? URLS.ICON_HEART_FILL : URLS.ICON_WISHLIST}
          alt={isChecked ? 'wish list checked' : 'unchecked wish list'}
        />
      )}
    </span>
  ) : null;
};

export default WishListIcon;
